import { moment } from "../date";
import type { AbsenceGeneralFormSchema } from "../schemas/absence";
import type { AbsenceList } from "../types/absences";
import type { Tables } from "../types/database.types";
import { getEmploymentForDate } from "./get-employment-for-date";

interface GetTakenVacationDaysProps {
	year: number;
	absences: AbsenceList;
	employments: Array<Tables<"employments">>;
	absenceSettings: AbsenceGeneralFormSchema;
}

export interface TakenVacationDays {
	total: number;
	past: number;
	upcoming: number;
}

/**
 * Count vacation days taken in a specific calendar year, split into days
 * already past and days still upcoming relative to today. Mirrors the
 * per-day deduction logic of `getAbsenceUsageByYear`: dates outside any
 * employment span and non-workdays are skipped; org-level half-vacation-days
 * are counted as 0.5. Rejected absences and non-balance-deducting absences
 * are excluded. Absences that straddle a year boundary are clipped to the
 * target year. Fractional totals from half-day rules are rounded to integers.
 *
 * @param props.year - The calendar year to count taken days for.
 * @param props.absences - Full absence list for the user.
 * @param props.employments - Employment records used to validate workdays and coverage.
 * @param props.halfVacationDays - Org-level recurring half-day dates (e.g. Dec 24).
 * @returns Day counts (may be fractional when half-day rules apply): total, past (before today), upcoming (today or later).
 */
export function getTakenVacationDays({
	year,
	absences,
	employments,
	absenceSettings,
}: GetTakenVacationDaysProps): TakenVacationDays {
	const today = moment();
	const todayDb = today.db();
	const yearStart = `${year}-01-01`;
	const yearEnd = `${year}-12-31`;

	let past = 0;
	let upcoming = 0;

	for (const absence of absences) {
		if (absence.state === "rejected") continue;
		if (!absence.deductsFromBalance) continue;

		const from = absence.startDate < yearStart ? yearStart : absence.startDate;
		const to = absence.endDate > yearEnd ? yearEnd : absence.endDate;

		if (from > to) continue;

		const dates = moment().rangeDatesDb(from, to);

		for (const date of dates) {
			const dateMoment = moment(date);
			const employment = getEmploymentForDate(employments, date);
			if (!employment) continue;
			if (!employment.workdays.includes(dateMoment.weekday())) continue;

			const month = Number(dateMoment.format("MM"));
			const day = Number(dateMoment.format("DD"));
			let deduction = 1;

			for (const hvd of absenceSettings.halfVacationDays) {
				if (hvd.month === month && hvd.day === day) {
					deduction = 0.5;
					break;
				}
			}

			if (date < todayDb) {
				past += deduction;
			} else {
				upcoming += deduction;
			}
		}
	}

	return { total: past + upcoming, past, upcoming };
}
